import { Plus, Trash2, Users } from 'lucide-react';
import { Link } from 'react-router';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { DAY_NAMES, DAY_SHORT, MONTH_NAMES, minutesToTime, timeToMinutes } from '../lib/format';
import type { Child, FamilySchedule, ScheduleBlock, YearlyPause } from '../lib/types';
import { useParent } from './ParentContext';

const ORDER = [1, 2, 3, 4, 5, 6, 0]; // Monday first
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const SUMMER: YearlyPause = { start: '06-15', end: '08-15' };

interface Saved {
  blocks: ScheduleBlock[];
  pause: YearlyPause | null;
}

/** A day of the year as month and day pickers; the value is 'MM-DD'. */
function MonthDay({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) {
  const [month, day] = value.split('-').map(Number) as [number, number];
  const pad = (n: number) => String(n).padStart(2, '0');
  const set = (m: number, d: number) => onChange(`${pad(m)}-${pad(Math.min(d, DAYS_IN_MONTH[m - 1]!))}`);
  return (
    <span className="pause-dates">
      <select value={month} onChange={(e) => set(Number(e.target.value), day)} aria-label={`${label} month`}>
        {MONTH_NAMES.map((m, i) => (
          <option key={m} value={i + 1}>
            {m}
          </option>
        ))}
      </select>
      <select value={day} onChange={(e) => set(month, Number(e.target.value))} aria-label={`${label} day`}>
        {Array.from({ length: DAYS_IN_MONTH[month - 1]! }, (_, i) => (
          <option key={i} value={i + 1}>
            {i + 1}
          </option>
        ))}
      </select>
    </span>
  );
}

export function ScheduleEditor({ child }: { child: Child }) {
  const { parent, refresh, toast } = useParent();
  const [loadedBlocks, setBlocks] = useState<ScheduleBlock[] | null>(null);
  const [pause, setPause] = useState<YearlyPause | null>(null);
  const [saved, setSaved] = useState('');
  const [busy, setBusy] = useState(false);

  const load = (r: Saved) => {
    setBlocks(r.blocks);
    setPause(r.pause);
    setSaved(JSON.stringify({ blocks: r.blocks, pause: r.pause }));
  };

  useEffect(() => {
    api.get<Saved>(`/api/children/${child.id}/schedule`).then(load);
  }, [child.id]);
  // The family school schedule (#248), to show whether this child is on it and offer to switch back.
  const [family, setFamily] = useState<FamilySchedule | null>(null);
  useEffect(() => {
    api.get<FamilySchedule>('/api/family-schedule').then(setFamily).catch(() => undefined);
  }, [child.id, saved]);

  if (!loadedBlocks) return <p className="muted">Loading…</p>;
  const blocks = loadedBlocks;
  const dirty = JSON.stringify({ blocks, pause }) !== saved;

  async function save() {
    const bad = blocks.find((b) => b.end <= b.start);
    if (bad) return toast({ text: `On ${DAY_NAMES[bad.day]}, a block ends before it starts.`, tone: 'alert' });
    if (pause && pause.start === pause.end) return toast({ text: 'The pause needs to last more than one day.', tone: 'alert' });
    setBusy(true);
    try {
      const sorted = [...blocks].sort((a, b) => a.day - b.day || a.start - b.start);
      load(await api.put<Saved>(`/api/children/${child.id}/schedule`, { blocks: sorted, pause }));
      await refresh();
      toast({ text: 'School hours saved.', tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <p className="muted">
        During school hours you can watch live, and blocked websites stay blocked. Times are in {parent.timezone.replace(/_/g, ' ')}.
        {child.mode !== 'auto' && <strong> Right now school time is set by hand, so the schedule is waiting.</strong>}
      </p>
      {family?.set && (
        <div className="family-schedule-note">
          <Users size={16} />
          {sameBlocks(blocks, family.blocks) ? (
            <span>
              On the <Link to="/settings#school-settings">family school schedule</Link>.
            </span>
          ) : (
            <>
              <span className="grow">
                {child.name}’s hours are different from the <Link to="/settings#school-settings">family school schedule</Link>.
              </span>
              <button className="btn small" onClick={() => setBlocks(family.blocks)}>
                Use family schedule
              </button>
            </>
          )}
        </div>
      )}
      <WeekEditor blocks={blocks} onChange={setBlocks} />
      <div className="pause-box">
        <label className="check">
          <input type="checkbox" checked={!!pause} onChange={(e) => setPause(e.target.checked ? SUMMER : null)} />
          <span>
            Pause school time every year
            <span className="muted small block">
              For summer break or other time off. Between these dates there’s no school time, so live view and website rules are off, unless you
              start school time by hand.
            </span>
          </span>
        </label>
        {pause && (
          <div className="pause-dates">
            <span className="muted">From</span>
            <MonthDay label="Pause starts" value={pause.start} onChange={(start) => setPause({ ...pause, start })} />
            <span className="muted">through</span>
            <MonthDay label="Pause ends" value={pause.end} onChange={(end) => setPause({ ...pause, end })} />
          </div>
        )}
        {child.policy.paused && <strong>School time is paused right now.</strong>}
      </div>
      <div className="save-bar">
        <button className="btn primary" disabled={!dirty || busy} onClick={save}>
          {busy ? 'Saving…' : dirty ? 'Save school hours' : 'Saved'}
        </button>
      </div>
    </section>
  );
}

/** Same weekly blocks, ignoring order. */
export const sameBlocks = (a: ScheduleBlock[], b: ScheduleBlock[]) => {
  const key = (bs: ScheduleBlock[]) => JSON.stringify([...bs].sort((x, y) => x.day - y.day || x.start - y.start).map((x) => [x.day, x.start, x.end]));
  return key(a) === key(b);
};

/** The week of school blocks, Monday first; used by a child's Schedule tab and the family school schedule (#248). */
export function WeekEditor({ blocks, onChange }: { blocks: ScheduleBlock[]; onChange: (blocks: ScheduleBlock[]) => void }) {
  const update = (i: number, patch: Partial<ScheduleBlock>) => onChange(blocks.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const remove = (i: number) => onChange(blocks.filter((_, j) => j !== i));
  const add = (day: number) => {
    const last = blocks.filter((b) => b.day === day).at(-1);
    const start = last ? Math.min(last.end + 30, 1380) : 510;
    onChange([...blocks, { day, start, end: Math.min(start + 120, 1440) }]);
  };
  const copyWeekdays = (day: number) => {
    const source = blocks.filter((b) => b.day === day);
    onChange([...blocks.filter((b) => b.day === 0 || b.day === 6 || b.day === day), ...[1, 2, 3, 4, 5].filter((d) => d !== day).flatMap((d) => source.map((b) => ({ ...b, day: d })))]);
  };

  return (
    <div className="week">
      {ORDER.map((day) => {
        const dayBlocks = blocks.map((b, i) => ({ b, i })).filter(({ b }) => b.day === day);
        return (
          <div key={day} className="day-row">
            <div className="day-name">
              <span className="wide-only">{DAY_NAMES[day]}</span>
              <span className="narrow-only">{DAY_SHORT[day]}</span>
            </div>
            <div className="day-blocks">
              {!dayBlocks.length && <span className="muted small">No school</span>}
              {dayBlocks.map(({ b, i }) => (
                <div key={i} className="block">
                  <input type="time" value={minutesToTime(b.start)} onChange={(e) => update(i, { start: timeToMinutes(e.target.value) })} aria-label="Starts" />
                  <span className="muted">to</span>
                  <input
                    type="time"
                    value={minutesToTime(b.end % 1440)}
                    onChange={(e) => update(i, { end: timeToMinutes(e.target.value) || 1440 })}
                    aria-label="Ends"
                  />
                  <button className="icon-btn" onClick={() => remove(i)} aria-label="Remove">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              <div className="day-tools">
                <button className="link-btn" onClick={() => add(day)}>
                  <Plus size={14} /> Add time
                </button>
                {day >= 1 && day <= 5 && dayBlocks.length > 0 && (
                  <button className="link-btn" onClick={() => copyWeekdays(day)}>
                    Use for all weekdays
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
